import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

interface AdminProfile {
  uid: string;
  name: string;
  email: string;
  authEmail: string;
  isAdmin: boolean;
}

export function UserManagementPage() {
  const { user } = useAuth();

  const [admins, setAdmins] = useState<AdminProfile[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [error, setError] = useState('');

  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<AdminProfile | 'not-found' | null>(null);
  const [granting, setGranting] = useState(false);
  const [grantSuccess, setGrantSuccess] = useState('');
  const [testingUid, setTestingUid] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAdmins = async () => {
    setLoadingAdmins(true);
    setError('');
    try {
      const snap = await getDocs(
        query(collection(db, 'profiles'), where('isAdmin', '==', true))
      );
      setAdmins(
        snap.docs.map((d) => ({
          uid: d.id,
          name: d.data().name ?? d.data().displayName ?? '',
          email: d.data().email ?? '',
          authEmail: d.data().authEmail ?? '',
          isAdmin: true,
        }))
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load admin users.');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleRevoke = async (uid: string, label: string) => {
    if (
      !window.confirm(
        `Revoke admin access for ${label}? They will immediately lose access to this portal.`
      )
    )
      return;
    setError('');
    try {
      await updateDoc(doc(db, 'profiles', uid), { isAdmin: false });
      setAdmins((prev) => prev.filter((a) => a.uid !== uid));
    } catch (err: any) {
      setError(err?.message || 'Failed to revoke admin access.');
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = searchEmail.trim().toLowerCase();
    if (!email) return;
    setSearching(true);
    setSearchResult(null);
    setGrantSuccess('');
    setError('');
    try {
      const [byAuthEmail, byAuthEmailLower, byEmail] = await Promise.all([
        getDocs(query(collection(db, 'profiles'), where('authEmail', '==', email))),
        getDocs(query(collection(db, 'profiles'), where('authEmail', '==', email.toLowerCase()))),
        getDocs(query(collection(db, 'profiles'), where('email', '==', email))),
      ]);
      const match = [byAuthEmail, byAuthEmailLower, byEmail].find((s) => !s.empty);
      if (!match) {
        setSearchResult('not-found');
      } else {
        const d = match.docs[0];
        setSearchResult({
          uid: d.id,
          name: d.data().name ?? d.data().displayName ?? '',
          email: d.data().email ?? email,
          authEmail: d.data().authEmail ?? email,
          isAdmin: d.data().isAdmin === true,
        });
      }
    } catch (err: any) {
      setError(err?.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const handleGrant = async () => {
    if (!searchResult || searchResult === 'not-found') return;
    const { uid, name, authEmail, email } = searchResult;
    const displayEmail = authEmail || email;
    if (
      !window.confirm(
        `Grant admin access to ${name || displayEmail}? They will be able to manage all accounts in this portal.`
      )
    )
      return;
    setGranting(true);
    setError('');
    try {
      await updateDoc(doc(db, 'profiles', uid), { isAdmin: true });
      const label = name || displayEmail;
      setGrantSuccess(`Admin access granted to ${label}.`);
      setSearchEmail('');
      setSearchResult(null);
      await loadAdmins();
    } catch (err: any) {
      setError(err?.message || 'Failed to grant admin access.');
    } finally {
      setGranting(false);
    }
  };

  const handleTestWhitelist = async (admin: AdminProfile) => {
    setTestingUid(admin.uid);
    try {
      const fn = httpsCallable(getFunctions(), 'checkCollegeWhitelist');
      const result = await fn({ targetUid: admin.uid, targetEmail: admin.authEmail });
      alert(JSON.stringify(result.data, null, 2));
    } catch (err: any) {
      alert('Error: ' + (err.message || String(err)));
    } finally {
      setTestingUid(null);
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 720 }}>
        <h1 className="page-title" style={{ marginBottom: 28 }}>User Management</h1>

        {error && <div className="error-message" style={{ marginBottom: 20 }}>{error}</div>}

        {/* Admin Users */}
        <div className="card" style={{ padding: 0, marginBottom: 24 }}>
          <div
            style={{
              padding: '18px 24px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2 style={{ fontSize: 14, fontWeight: 600 }}>Admin Users</h2>
            {!loadingAdmins && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {admins.length} {admins.length === 1 ? 'user' : 'users'}
              </span>
            )}
          </div>

          {loadingAdmins ? (
            <div className="loading">Loading...</div>
          ) : admins.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
              No admin users found
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>UID</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.uid}>
                      <td style={{ fontWeight: 500 }}>
                        {admin.name || (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                        {admin.uid === user?.uid && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: 11,
                              color: 'var(--accent-text)',
                              fontWeight: 400,
                            }}
                          >
                            (you)
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          color: 'var(--text-secondary)',
                          fontFamily: 'monospace',
                          fontSize: 13,
                        }}
                      >
                        {admin.authEmail || admin.email || '—'}
                      </td>
                      <td
                        style={{
                          color: 'var(--text-muted)',
                          fontFamily: 'monospace',
                          fontSize: 11,
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {admin.uid}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            className="btn-secondary"
                            style={{ padding: '4px 12px', fontSize: 12 }}
                            disabled={testingUid === admin.uid}
                            onClick={() => handleTestWhitelist(admin)}
                          >
                            {testingUid === admin.uid ? 'Testing...' : 'Test Whitelist'}
                          </button>
                          {admin.uid !== user?.uid && (
                            <button
                              className="btn-danger"
                              style={{ padding: '4px 12px', fontSize: 12 }}
                              onClick={() =>
                                handleRevoke(admin.uid, admin.name || admin.authEmail || admin.email || admin.uid)
                              }
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Grant Admin Access */}
        <div className="card">
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Grant Admin Access
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
            Search for a registered Klarr user by email address to grant admin access to
            this portal.
          </p>

          <form
            onSubmit={handleSearch}
            style={{ display: 'flex', gap: 10, marginBottom: 16 }}
          >
            <input
              className="form-input"
              type="email"
              value={searchEmail}
              onChange={(e) => {
                setSearchEmail(e.target.value);
                setSearchResult(null);
                setGrantSuccess('');
              }}
              placeholder="user@example.com"
              style={{ flex: 1 }}
            />
            <button
              type="submit"
              className="btn-secondary"
              disabled={searching || !searchEmail.trim()}
              style={{ flexShrink: 0 }}
            >
              {searching ? 'Searching...' : 'Find User'}
            </button>
          </form>

          {searchResult === 'not-found' && (
            <div className="error-message">
              No Klarr account found with that email address.
            </div>
          )}

          {searchResult && searchResult !== 'not-found' && (
            <div
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>
                  {searchResult.name || (
                    <span style={{ color: 'var(--text-muted)' }}>(no display name)</span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    fontFamily: 'monospace',
                  }}
                >
                  {searchResult.uid}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                <button
                  className="btn-secondary"
                  disabled={testingUid === searchResult.uid}
                  onClick={() => handleTestWhitelist(searchResult)}
                >
                  {testingUid === searchResult.uid ? 'Testing...' : 'Test Whitelist'}
                </button>
                {searchResult.isAdmin ? (
                  <span className="badge badge-active">Already an admin</span>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={handleGrant}
                    disabled={granting}
                  >
                    {granting ? 'Granting...' : 'Grant Admin'}
                  </button>
                )}
              </div>
            </div>
          )}

          {grantSuccess && (
            <div className="success-message" style={{ marginTop: 14 }}>
              {grantSuccess}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
